import { useQuery, useQueryClient } from '@tanstack/react-query';
import { throttle } from 'es-toolkit';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { create } from 'zustand';

import { MealPhotoQueueResponse } from '@/api/generated/MNT';
import { ApiRegion } from '@/config';
import { useAuth } from '@/context/appContext';
import { useFeatures } from '@/context/FeatureContext';
import { useRegionalApis } from '@/context/RegionScope';
import { useInterval } from '@/hooks/useInterval';
import { useSocketIOClient } from '@/socketio/SocketIOService';
import { useDocumentVisibility } from '@/utils';
import { resolveQueueItemRegion } from '@/utils/queueItemRoute';

const filterRecentlyLabelled = (recentlyLabelledQueueIDs: number[], queues: MealPhotoQueueResponse[]) => {
  return queues.filter(q => !recentlyLabelledQueueIDs.includes(q.id));
};

/** One throttle per region, so the Canada background refetch never swallows a US refetch. */
const makeRefetchThrottle = () => {
  const state = {
    lastFunc: null as (() => void) | null,
    call: (func: () => void) => {
      state.lastFunc = func;
      state.run();
    },
    run: throttle(() => {
      state.lastFunc?.();
    }, 500),
  };
  return state;
};

const refetchThrottles: Record<ApiRegion, ReturnType<typeof makeRefetchThrottle>> = {
  [ApiRegion.CA]: makeRefetchThrottle(),
  [ApiRegion.US]: makeRefetchThrottle(),
};

// Canada keeps its original storage key.
const lastRefetchStorageKey = (region: ApiRegion) =>
  region === ApiRegion.CA ? 'mpq-refetch:last-refetch' : `mpq-refetch:last-refetch:${region}`;

const lastRefetchReasons: Partial<Record<ApiRegion, string | null>> = {};
const useLastRefetchedAtStore = create(() => ({
  byRegion: {} as Partial<Record<ApiRegion, number>>,
}));

/**
 * `rendersQueryState: false` is for callers that only use `refetch`. TanStack Query re-renders an observer
 * that reads no fields on every status change, which re-rendered the whole app (through the shell's
 * periodic refetch) twice per poll.
 */
const useAvailableMqpsQuery = ({ rendersQueryState = true }: { rendersQueryState?: boolean } = {}) => {
  const { authInfo } = useAuth();
  const { region, dataReviewApi } = useRegionalApis();
  const queryClient = useQueryClient();
  const reviewerId = authInfo?.reviewer_id;
  const { queue_polling: queuePolling } = useFeatures();

  const isDocumentVisible = useDocumentVisibility();
  // Not region-qualified on purpose: each region has its own QueryClient (RegionScope).
  const query = useQuery({
    queryKey: ['available-mpqs', reviewerId],
    queryFn: async () => {
      useLastRefetchedAtStore.setState(state => ({ byRegion: { ...state.byRegion, [region]: Date.now() } }));
      if (!authInfo) {
        return {
          queues: [],
          ownedQueueCount: 0,
          ownedQueues: [],
          availableQueueCount: 0,
        };
      }

      const reason = lastRefetchReasons[region] ?? null;
      const res = await dataReviewApi.appApiDataReviewerGetDataReviewerMealPhotoQueuesToBeProcessed(
        {
          data_reviewer_id: authInfo.reviewer_id,
        },
        {
          params: {
            'refetch-reason': reason,
          },
        },
      );
      const activeMpqs = res.data;
      const ownedQueues = activeMpqs.filter(i => i.first_reviewer_user_id === authInfo.reviewer_id);
      const availableQueues = activeMpqs.filter(i => !i.first_reviewer_user_id);

      return {
        queues: activeMpqs || [],
        ownedQueueCount: ownedQueues.length,
        ownedQueues: ownedQueues,
        availableQueueCount: availableQueues.length,
      };
    },
    enabled: queuePolling,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...(rendersQueryState ? {} : { notifyOnChangeProps: [] }),
  });

  // Stable across useQuery result-object churn so debounced socket handlers are
  // not cancelled/recreated on every available-mpqs observer render.
  const refetch = React.useCallback(
    (reason: string) => {
      if (!queuePolling) {
        return;
      }
      // If tab is not active and it's been less than 15s since the last refetch, skip refetch.
      const lastRefetch = localStorage.getItem(lastRefetchStorageKey(region));
      const lastRefetchTimestamp = lastRefetch ? +lastRefetch : null;
      const refetchThrottle = lastRefetchTimestamp && Date.now() - lastRefetchTimestamp < 15 * 1000;
      if (refetchThrottle && (!isDocumentVisible || reason == 'initial')) {
        return;
      }
      localStorage.setItem(lastRefetchStorageKey(region), Date.now().toString());
      // The throttle is module-level and keeps only the last caller, who may have
      // unmounted by the time it fires. Refetch whatever queue queries are still
      // watched in this region: none after unmount or logout, so a cleared cache
      // stays empty; any mounted consumer still gets its refetch.
      refetchThrottles[region].call(() => {
        lastRefetchReasons[region] = reason;
        void queryClient.refetchQueries({ queryKey: ['available-mpqs', reviewerId], type: 'active' });
      });
    },
    [queryClient, isDocumentVisible, region, reviewerId, queuePolling],
  );

  return {
    query,
    refetch,
  };
};

/**
 * When this region's queue fetch last started. Kept out of the queue hooks above: every queue reader
 * (the app shell, the sidebar, one icon per queue row) re-rendered at the start of each poll when they
 * all subscribed to it, and only useGetNextQueue needs it.
 */
export const useQueueLastRefetchedAt = (): number | null => {
  const { region } = useRegionalApis();
  return useLastRefetchedAtStore(state => state.byRegion[region] ?? null);
};

/**
 * Returns the current size of the "to be processed" backlog (all active MPQs for
 * the reviewer's groups), or null if it hasn't been fetched yet. Reads the shared
 * `available-mpqs` query cache, so it does not trigger an extra fetch.
 */
export const useQueueBacklogCount = (): number | null => {
  const { query } = useAvailableMqpsQuery();
  return query.data ? query.data.queues.length : null;
};

export const useActiveQueuePeriodRefetch = () => {
  // Only the connected bit — telemetry must not restart this interval.
  const connected = useSocketIOClient().connected;
  const { queue_polling: queuePolling } = useFeatures();
  const { refetch } = useAvailableMqpsQuery({ rendersQueryState: false });
  const refetchInterval = !queuePolling ? null : connected ? 60 * 1000 : 6 * 1000;
  useInterval(() => {
    refetch('interval:' + String((refetchInterval ?? 0) / 1000));
  }, refetchInterval);
};

export const useMealQueueService = () => {
  const { authInfo } = useAuth();
  const { region } = useRegionalApis();
  const routerLocation = useLocation();
  const [recentlyLabelledQueueIDs, setRecentlyLabelledQueueIDs] = useState<number[]>([]);

  const { query, refetch } = useAvailableMqpsQuery();
  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  useEffect(() => {
    refetch('initial');
  }, []);

  const queues = query.data?.queues;
  const ownedQueuesData = query.data?.ownedQueues;
  const ownedCount = query.data?.ownedQueueCount || 0;
  const availableCount = query.data?.availableQueueCount || 0;
  const isPending = query.isPending;
  const isError = query.isError;

  const getIsCurrentlyLabellingOwnQueue = React.useCallback(
    (location: { pathname: string; search: string }) => {
      // A US item with the same numeric id as a Canada queue must not silence Canada alerts.
      if (resolveQueueItemRegion(new URLSearchParams(location.search), location.pathname) !== region) {
        return false;
      }
      return !!queues?.some(
        q => q.first_reviewer_user_id == authInfo?.reviewer_id && location.pathname == `/queue-item/${q.id}`,
      );
    },
    [queues, authInfo?.reviewer_id, region],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  return React.useMemo(
    () => ({
      query,
      refetch,
      allActiveQueues: filterRecentlyLabelled(recentlyLabelledQueueIDs, queues || []),
      ownedCount,
      ownedQueues: filterRecentlyLabelled(recentlyLabelledQueueIDs, ownedQueuesData || []),
      availableCount,
      currentlyLabellingOwnQueue: getIsCurrentlyLabellingOwnQueue(routerLocation),
      addRecentlyLabelledQueueId: (queueId: number) => {
        setRecentlyLabelledQueueIDs([queueId, ...recentlyLabelledQueueIDs].slice(0, 5));
      },
      // Depend on data/status fields — not the useQuery result object identity, which
      // changes every observer render and was defeating this memo.
    }),
    [
      queues,
      ownedQueuesData,
      ownedCount,
      availableCount,
      isPending,
      isError,
      refetch,
      getIsCurrentlyLabellingOwnQueue,
      routerLocation,
      recentlyLabelledQueueIDs,
    ],
  );
};
